import Swiper from "swiper";
import {Navigation, Pagination} from 'swiper/modules';

export function slideDown(element, duration = 200, easing = 'linear') {
  element.style.display = 'block';
  let height = element.scrollHeight;
  element.style.height = '0px';
  element.style.overflow = 'hidden';

  const easingFunctions = {
    linear: function (t) {
      return t;
    },
    easeIn: function (t) {
      return t * t;
    },
    easeOut: function (t) {
      return t * (2 - t);
    },
    easeInOut: function (t) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    },
  };

  let startTime = null;

  function animate(time) {
    if (!startTime) {
      startTime = time;
    }
    let timeElapsed = time - startTime;
    let progress = Math.min(timeElapsed / duration, 1);
    let easeProgress = easingFunctions[easing](progress);

    element.style.height = height * easeProgress + 'px';

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      element.style.height = 'auto';
      element.style.overflow = '';
    }
  }

  requestAnimationFrame(animate);
}

export function slideUp(element, duration = 200, easing = 'linear') {
  if (!element) return;
  let height = element.scrollHeight;
  element.style.height = height + 'px';
  element.style.overflow = 'hidden';

  const easingFunctions = {
    linear: function (t) {
      return t;
    },
    easeIn: function (t) {
      return t * t;
    },
    easeOut: function (t) {
      return t * (2 - t);
    },
    easeInOut: function (t) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    },
  };

  let startTime = null;

  function animate(time) {
    if (!startTime) {
      startTime = time;
    }
    let timeElapsed = time - startTime;
    let progress = Math.min(timeElapsed / duration, 1);
    let easeProgress = easingFunctions[easing](progress);

    element.style.height = height * (1 - easeProgress) + 'px';

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      element.style.display = 'none';
      element.style.height = '';
      element.style.overflow = '';
    }
  }

  requestAnimationFrame(animate);
}

export function lockBody() {
  const body = document.querySelector('body');
  const scrollBarWidth = window.innerWidth - body.clientWidth;

  body.style.overflow = 'hidden';
  body.style.paddingRight = `${scrollBarWidth}px`;
}

export function unLockBody() {
  const body = document.querySelector('body');
  body.style.overflow = 'visible';
  body.style.paddingRight = '0px';
}

export function watchElement(selectorOrElement, callback, watchAlways = false) {
  let element;

  if (typeof selectorOrElement === 'string') {
    element = document.querySelector(selectorOrElement);
  } else if (selectorOrElement instanceof Element) {
    element = selectorOrElement;
  }

  if (!element) {
    return null;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('see-element-js');
        if (callback) callback(entry.target, true);
        if (!watchAlways) observer.unobserve(entry.target);
      } else {
        entry.target.classList.remove('see-element-js');
        if (watchAlways && callback) callback(entry.target, false);
      }
    });
  });

  observer.observe(element);

  // return controller
  return {
    destroy() {
      observer.disconnect();
    },
  };
}

export function scrollToEl(linkHREF, setTimeOut = 400) {
  const anchorSection =
    typeof linkHREF === 'object' ? linkHREF : document.querySelector(`${linkHREF}`);
  const headerHeight = document.querySelector('.header')?.offsetHeight || 0;

  if (anchorSection) {
    setTimeout(function () {
      const offsetTop =
        anchorSection.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }, setTimeOut);
  }
}

/**
 * return string clamp(min, preferred, max) to rem
 * @param {number} valMax - value max  (px)
 * @param {number} valMin - value min (px)
 * @param {number} wMax - viewport max (px)
 * @param {number} wMin - viewport min (px)
 * @returns {string}
 */
export function generateClamp(valMax, valMin, wMax = 1920, wMin = 767) {
  const args = [valMax, valMin, wMax, wMin];
  if (args.some(arg => typeof arg !== 'number' || isNaN(arg) || !isFinite(arg))) {
    console.error(`generateClamp() -> Invalid numeric arguments`, args);
    return '';
  }

  if (valMin > valMax || wMin > wMax) {
    console.error(`generateClamp() -> min/max values are inverted`);
    return '';
  }

  const slope = (valMax - valMin) / (wMax - wMin);
  const intercept = valMin - slope * wMin;

  const slopeVW = slope * 100;
  const interceptREM = intercept / 16;
  const minREM = valMin / 16;
  const maxREM = valMax / 16;

  return `clamp(${minREM}rem, calc(${slopeVW}vw + ${interceptREM}rem), ${maxREM}rem)`;
}

export function activateSliderOnMobile(el, breakpoint, sliderOptions = {}, defaultArrows = true, defaultPagination = true, swiperParent = 'section') {
  const sliders = document.querySelectorAll(el);
  if (!sliders.length) return;

  sliders.forEach((sliderEl) => {
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint})`);
    let sliderSwiper = null;

    function windowSizeIsChange() {
      if (mediaQuery.matches) {
        activateSlider();
      } else {
        unActivateSlider();
      }
    }

    windowSizeIsChange();

    mediaQuery.addEventListener
      ? mediaQuery.addEventListener("change", windowSizeIsChange)
      : mediaQuery.addListener(windowSizeIsChange);

    function activateSlider() {
      if (sliderSwiper || sliderEl.dataset.initialized === "true") return; // don't double-init
      sliderEl.dataset.initialized = "true";

      sliderEl.classList.add("swiper");

      // check if wrapper already exists
      let wrapper = sliderEl.querySelector(".swiper-wrapper");
      if (!wrapper) {
        // add slide class to children
        [...sliderEl.children].forEach((childSlide) =>
          childSlide.classList.add("swiper-slide"),
        );

        // create wrapper
        wrapper = document.createElement("div");
        wrapper.classList.add("swiper-wrapper");

        while (sliderEl.firstChild) {
          wrapper.appendChild(sliderEl.firstChild);
        }
        sliderEl.appendChild(wrapper);
      } else {
        // if wrapper already exists → just make sure slides have class
        [...wrapper.children].forEach((childSlide) =>
          childSlide.classList.add("swiper-slide"),
        );
      }

      // arrows
      let navigation = {};
      if (defaultArrows !== "remove") {
        if (defaultArrows === true) {
          if (!sliderEl.querySelector(".swiper-button-prev")) {
            const arrows = `
          <button class="swiper-button-prev"></button>
          <button class="swiper-button-next"></button>`;
            sliderEl.insertAdjacentHTML("beforeend", arrows);
          }

          navigation = {
            prevEl: sliderEl.querySelector(".swiper-button-prev"),
            nextEl: sliderEl.querySelector(".swiper-button-next"),
          };
        } else {
          const prevButton = sliderEl.closest(`${swiperParent}`)?.querySelector(".swiper-button-prev");
          const nextButton = sliderEl.closest(`${swiperParent}`)?.querySelector(".swiper-button-next");

          if (prevButton && nextButton) {
            prevButton.style.display = "block";
            nextButton.style.display = "block";
            navigation = {prevEl: prevButton, nextEl: nextButton};
          }
        }
      }

      // pagination
      let pagination = {};
      if (defaultPagination !== "remove") {
        if (defaultPagination === true) {
          if (!sliderEl.querySelector(".swiper-pagination")) {
            sliderEl.insertAdjacentHTML("beforeend", `<div class="swiper-pagination"></div>`);
          }
          pagination = {
            el: sliderEl.querySelector(".swiper-pagination"),
            type: "bullets",
            clickable: true,
          };
        } else {
          const paginationEl = sliderEl.closest(`${swiperParent}`)?.querySelector(".swiper-pagination");
          if (paginationEl) {
            paginationEl.style.display = "flex";
            paginationEl.style.position = "static";
            pagination = {el: paginationEl, clickable: true};
          }
        }
      }

      // init Swiper
      sliderSwiper = new Swiper(sliderEl, {
        modules: [Navigation, Pagination],
        speed: 600,
        navigation,
        pagination,
        ...sliderOptions,
        on: {
          init: (swiper) => {
            setTimeout(() => {
              if (swiper && swiper.pagination) {
                swiper.pagination.render();
                swiper.pagination.update();
              }
            }, 100);
          },
        },
      });
    }

    function unActivateSlider() {
      if (!sliderSwiper) return;

      sliderSwiper.destroy();
      sliderSwiper = null;
      sliderEl.dataset.initialized = "false";

      sliderEl.classList.remove("swiper");

      // unwrap slides
      const wrapper = sliderEl.querySelector(".swiper-wrapper");
      if (wrapper) {
        while (wrapper.firstChild) {
          sliderEl.appendChild(wrapper.firstChild);
        }
        wrapper.remove();
      }

      // remove classes
      sliderEl.querySelectorAll(".swiper-slide").forEach((el) => el.classList.remove("swiper-slide"));

      // cleanup arrows
      if (defaultArrows !== "remove") {
        if (defaultArrows === true) {
          sliderEl.querySelector(".swiper-button-prev")?.remove();
          sliderEl.querySelector(".swiper-button-next")?.remove();
        } else {
          const prevButton = sliderEl.closest(`${swiperParent}`)?.querySelector(".swiper-button-prev");
          const nextButton = sliderEl.closest(`${swiperParent}`)?.querySelector(".swiper-button-next");
          if (prevButton) prevButton.style.display = "none";
          if (nextButton) nextButton.style.display = "none";
        }
      }

      // cleanup pagination
      if (defaultPagination !== "remove") {
        if (defaultPagination === true) {
          sliderEl.querySelector(".swiper-pagination")?.remove();
        } else {
          const paginationEl = sliderEl.closest(`${swiperParent}`)?.querySelector(".swiper-pagination");
          if (paginationEl) paginationEl.style.display = "none";
        }
      }
    }
  });
}

// activateSliderOnMobile(
//   ".slider-on-mobile",
//   "767px",
//   {
//     spaceBetween: "24px",
//   },
//   true,
//   "remove"
// );

/**
 * Watches a specific CSS media query breakpoint and invokes a callback
 * with the current match status (`true` or `false`) whenever the match state changes.
 *
 * @param {string} breakpoint - A valid CSS media query string (e.g., "(max-width: 640px)").
 * @param {(matches: boolean) => void} callback - A function to be called when the media query matches or unmatches.
 *        It receives a boolean indicating the current match state.
 *
 * @example
 * mediaQueryWatcher("(max-width: 768px)", (matches) => {
 *   if (matches) {
 *     console.log("Viewport is 768px or less");
 *   } else {
 *     console.log("Viewport is more than 768px");
 *   }
 * });
 *
 * @note
 * This function performs an immediate check upon initialization and then continues
 * to listen for changes in match state using `matchMedia.addEventListener`.
 */
export function mediaQueryWatcher(breakpoint, callback) {
  const mediaQuery = window.matchMedia(breakpoint);

  const checkWindowSize = () => callback(mediaQuery.matches);

  // Initial check
  checkWindowSize();

  // React to changes
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener("change", checkWindowSize);
  } else if (mediaQuery.addListener) {
    mediaQuery.addListener(checkWindowSize);
  }
}
